import { Formik } from "formik";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { CHANGES_APPLIED } from "@src/common/helpers/toastIds";
import {
  BaseBoxBody,
  BaseBoxHeader,
  StyledBoxWrapper,
} from "@src/modules/DeviceStatus/Devices/DeviceInfoView/ResultsView/ResultsView.components";
import useTestResults from "@src/modules/DeviceStatus/Devices/DeviceInfoView/ResultsView/hooks/useTestResults";
import CustomResultsForm from "@src/modules/shared/components/Results/CustomResultsForm";
import { ImageWrapper } from "@src/modules/shared/components/Results/Results.components";
import ResultsFormHeader from "@src/modules/shared/components/Results/ResultsFormHeader";
import useEditCustomTestResults from "@src/modules/shared/components/Results/hooks/useEditCustomTestResults";
import useStudyImages from "@src/modules/shared/components/Results/hooks/useStudyImages";
import SmallLoader from "@src/modules/shared/components/ScreenLoading/SmallLoader";
import { StudyTestResult, TestStatus } from "@src/modules/shared/types";
import {
  ImagesBoxBody,
  StyledDicomImages,
} from "@src/modules/shared/components/Results/CustomResults.components";
import CustomTestFileInput from "@src/modules/shared/components/Results/CustomTestFileInput";
import ImagesRange from "@src/modules/shared/components/Results/ImagesRange";

export interface EditCustomResultsForm {
  results: EditCustomResultsFormValue[];
  studyId: string;
  testId: string;
}
export interface EditCustomResultsFormValue {
  label: string;
  value?: string;
  isPassed: boolean;
}

interface Props {
  selectedStudyId: string;
  selectedTest: StudyTestResult;
  isEditingAvailable: boolean;
  hasImageRangeEditing: boolean;
  hasDicoms: boolean;
  imagesDisabled?: boolean;
}

const CustomResults: FunctionComponent<Props> = ({
  selectedStudyId,
  selectedTest,
  isEditingAvailable,
  hasImageRangeEditing,
  hasDicoms,
  imagesDisabled = false,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: testResultsData, isLoading } = useTestResults(
    selectedStudyId,
    selectedTest.id,
  );

  const [isEdit, setIsEdit] = useState<boolean>(
    selectedTest.resultStatus === TestStatus.ActionNeeded,
  );

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      setIsEdit(false);
    }
  }, [selectedTest.testId, selectedStudyId]);

  const editResultsMutation = useEditCustomTestResults(
    selectedStudyId,
    selectedTest.id,
  );

  const { data: studyImagesData } = useStudyImages(
    selectedStudyId,
    selectedTest.id,
  );

  const initialValues: EditCustomResultsFormValue[] =
    testResultsData && testResultsData.results.length > 0
      ? testResultsData.results.map(({ value, isFailed, label }) => ({
          value,
          isPassed: !isFailed,
          label,
        }))
      : [{ label: "", value: "", isPassed: false }];

  const validationSchema: Yup.SchemaOf<EditCustomResultsForm> =
    Yup.object().shape({
      results: Yup.array()
        .of(
          Yup.object().shape({
            isPassed: Yup.mixed().optional(),
            label: Yup.string().max(200).required(),
            value: Yup.string().max(2000).required(),
          }),
        )
        .required(),
      studyId: Yup.mixed().required(),
      testId: Yup.mixed().optional(),
    });

  const handleSubmit = (model: EditCustomResultsForm) => {
    editResultsMutation.mutate(model.results, {
      onSuccess: () => {
        toast.success(t("Changes successfully saved"), {
          toastId: CHANGES_APPLIED,
        });
        setIsEdit(false);
        queryClient.invalidateQueries();
      },
    });
  };

  const handleImagesUploaded = () => {
    queryClient.invalidateQueries();
  };

  const images = studyImagesData?.map((base64, index) => (
    <ImageWrapper key={index}>
      <img
        src={`data:image/jpeg;base64,${base64}`}
        alt={`dicom image ${index}`}
      />
    </ImageWrapper>
  ));

  return (
    <StyledBoxWrapper>
      <Formik
        initialValues={{
          results: initialValues,
          studyId: selectedStudyId,
          testId: selectedTest.id,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {(formProps) => (
          <>
            <BaseBoxHeader>
              <ResultsFormHeader
                selectedStudyId={selectedStudyId}
                selectedTest={selectedTest}
                isEditingAvailable={isEditingAvailable}
                isEdit={isEdit}
                setIsEdit={setIsEdit}
                onSave={formProps.submitForm}
                onCancel={formProps.resetForm}
              />
            </BaseBoxHeader>
            <BaseBoxBody>
              {isLoading && <SmallLoader />}
              {testResultsData && (
                <CustomResultsForm
                  {...formProps}
                  selectedStudyId={selectedStudyId}
                  selectedTest={selectedTest}
                  isEdit={isEdit}
                />
              )}
              {hasImageRangeEditing && hasDicoms && (
                <ImagesRange
                  selectedStudyId={selectedStudyId}
                  selectedTestId={selectedTest.id}
                  isCustomTest={true}
                />
              )}
            </BaseBoxBody>
            {!imagesDisabled && (
              <ImagesBoxBody>
                <StyledDicomImages>
                  {!!images && images.length > 0
                    ? images
                    : !hasImageRangeEditing && (
                        <CustomTestFileInput
                          {...formProps}
                          handleImagesUploaded={handleImagesUploaded}
                        />
                      )}
                </StyledDicomImages>
              </ImagesBoxBody>
            )}
          </>
        )}
      </Formik>
    </StyledBoxWrapper>
  );
};

export default CustomResults;
